import { Fragment, useState } from 'react';
import { ChevronDown, ChevronUp, ClipboardList } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ApplicantDetails } from '@/api/schema';
import { Panel, Toolbar } from '@/components/layout/Page';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';
import { ErrorState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Spinner } from '@/components/ui/Spinner';
import { Table, TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { cx } from '@/lib/cx';
import { formatDate, type DateFormat } from '@/lib/dates';
import { useApplications, useApplicationJobs } from '../api';
import { APPLICANT_DETAIL_FIELDS } from '../applicationFields';
import styles from './ApplicationsPanel.module.css';

function Background({ details }: { details: ApplicantDetails | undefined }) {
  return (
    <dl className={styles.background}>
      {APPLICANT_DETAIL_FIELDS.map(({ name, label, type }) => {
        const value = details?.[name];
        return value === undefined || value === null || value === '' ? null : (
          <div key={name}>
            <dt>{label}</dt>
            <dd>
              {value}
              {type === 'number' ? ' years' : ''}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

const PER_PAGE = 20;

export function ApplicationsPanel({
  candidateId,
  dateFormat,
}: {
  candidateId?: string;
  dateFormat?: DateFormat;
}) {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const vacancy = params.get('vacancy') ?? '';
  const vacancyId =
    !candidateId && /^[a-f0-9]{8}(-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(vacancy) ? vacancy : '';
  function setVacancyId(id: string) {
    const next = new URLSearchParams(params);
    if (id) next.set('vacancy', id);
    else next.delete('vacancy');
    setParams(next);
    setPage(1);
    setExpandedId(null);
  }
  const jobs = useApplicationJobs(!candidateId);
  const applications = useApplications(page, candidateId, vacancyId || undefined);
  return (
    <Panel
      title="Website applications"
      icon={<ClipboardList size={17} aria-hidden="true" />}
      flush={!candidateId}
    >
      {!candidateId ? (
        <Toolbar>
          <Field label="Filter by job" className={cx(styles.jobFilter)}>
            {(ids) => (
              <Select
                {...ids}
                value={vacancyId}
                placeholder="All jobs"
                disabled={!jobs.data}
                options={(jobs.data ?? []).map((job) => ({
                  value: job.id,
                  label: [job.title, job.country].filter(Boolean).join(' · '),
                }))}
                onChange={(event) => {
                  setVacancyId(event.target.value);
                }}
              />
            )}
          </Field>
          {vacancyId ? (
            <Button
              variant="ghost"
              className={styles.clearFilter}
              onClick={() => {
                setVacancyId('');
              }}
            >
              Clear filter
            </Button>
          ) : null}
          {jobs.isError ? (
            <ErrorState
              message="Job filter choices could not be loaded."
              onRetry={() => {
                void jobs.refetch();
              }}
            />
          ) : null}
        </Toolbar>
      ) : null}
      {applications.isPending ? (
        <div className={candidateId ? undefined : styles.listState}>
          <Spinner label="Loading applications" />
        </div>
      ) : applications.isError ? (
        <div className={candidateId ? undefined : styles.listState}>
          <ErrorState
            message="Applications could not be loaded."
            onRetry={() => {
              void applications.refetch();
            }}
          />
        </div>
      ) : applications.data.total === 0 ? (
        <p className={cx(styles.empty, !candidateId && styles.listState)}>
          No applications received
          {candidateId
            ? ' for this candidate'
            : vacancyId
              ? ' for this job'
              : ' yet. Submissions from the public Careers page appear here'}
          .
        </p>
      ) : (
        <>
          {candidateId ? (
            <div className={styles.applications}>
              {applications.data.items.map((item) => (
                <article
                  key={item.id}
                  className={styles.application}
                  aria-label={`Application for ${item.job_title}`}
                >
                  <div className={styles.applicationHeader}>
                    <h3>{item.job_title}</h3>
                    <time className={styles.date} dateTime={item.created_at}>
                      Received {formatDate(item.created_at, dateFormat)}
                    </time>
                  </div>
                  {item.message ? <p className={styles.message}>{item.message}</p> : null}
                  <Background details={item.details} />
                </article>
              ))}
            </div>
          ) : (
            <div className={styles.applicationList}>
              <TableWrap>
                <Table minWidth={660}>
                  <thead>
                    <Tr>
                      <Th label="Candidate" />
                      <Th label="Job applied for" />
                      <Th label="Received" />
                      <Th label="Application" className={styles.actions} />
                    </Tr>
                  </thead>
                  <tbody>
                    {applications.data.items.map((item) => {
                      const expanded = expandedId === item.id;
                      const detailsId = `application-details-${item.id}`;
                      const hasBackground = APPLICANT_DETAIL_FIELDS.some(({ name }) => {
                        const value = item.details?.[name];
                        return value != null && value !== '';
                      });
                      const ExpandIcon = expanded ? ChevronUp : ChevronDown;
                      return (
                        <Fragment key={item.id}>
                          <Tr selected={expanded}>
                            <Td>
                              <Link
                                className={styles.candidate}
                                to={`/candidates/${item.candidate_id}`}
                              >
                                <Avatar name={item.full_name} size={32} />
                                <span>{item.full_name}</span>
                              </Link>
                            </Td>
                            <Td className={styles.jobTitle}>{item.job_title}</Td>
                            <Td>
                              <time className={styles.received} dateTime={item.created_at}>
                                {formatDate(item.created_at, dateFormat)}
                              </time>
                            </Td>
                            <Td className={styles.actions}>
                              {item.message || hasBackground ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconAfter={<ExpandIcon size={15} aria-hidden="true" />}
                                  aria-expanded={expanded}
                                  aria-controls={expanded ? detailsId : undefined}
                                  onClick={() => {
                                    setExpandedId(expanded ? null : item.id);
                                  }}
                                >
                                  {expanded ? 'Hide details' : 'View details'}
                                </Button>
                              ) : (
                                <span className={styles.empty}>No extra details</span>
                              )}
                            </Td>
                          </Tr>
                          {expanded ? (
                            <Tr>
                              <Td id={detailsId} colSpan={4} className={styles.detailCell}>
                                <div className={styles.detailContent}>
                                  {item.message ? (
                                    <section>
                                      <h3>Candidate message</h3>
                                      <p className={styles.message}>{item.message}</p>
                                    </section>
                                  ) : null}
                                  {hasBackground ? (
                                    <section>
                                      <h3>Background</h3>
                                      <Background details={item.details} />
                                    </section>
                                  ) : null}
                                </div>
                              </Td>
                            </Tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
            </div>
          )}
          {!candidateId || applications.data.total > PER_PAGE ? (
            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={applications.data.total}
              label="applications"
              onChange={(next) => {
                setPage(next);
                setExpandedId(null);
              }}
            />
          ) : null}
        </>
      )}
    </Panel>
  );
}
